import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { api, User } from "../lib/api";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    username: string,
    password: string,
    role?: "student" | "parent" | "teacher" | "school_admin" | "admin",
  ) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const isRegistering = React.useRef(false);

  const fetchUserProfile = async () => {
    try {
      console.log("[AuthContext] Fetching user profile...");
      const profile = await api.getCurrentUser();
      console.log("[AuthContext] User profile fetched:", profile);
      setUser(profile);
      return true;
    } catch (error) {
      console.error("[AuthContext] Failed to fetch user profile:", error);
      console.error(
        "[AuthContext] Error details:",
        error instanceof Error ? error.message : error,
      );
      // If profile doesn't exist, user needs to register
      throw error;
    }
  };

  useEffect(() => {
    console.log("[AuthContext] Initializing auth state...");

    // Check for existing session synchronously
    supabase.auth
      .getSession()
      .then(async ({ data: { session } }) => {
        console.log("[AuthContext] Session check complete:", !!session);

        if (session?.user) {
          console.log("[AuthContext] Session found for:", session.user.email);

          // Fetch full profile - WAIT for it to get the correct role
          try {
            await fetchUserProfile();
            console.log("[AuthContext] Profile loaded with role:", user?.role);
          } catch (err) {
            console.warn(
              "[AuthContext] Profile fetch failed, using fallback:",
              err,
            );
            // Only use fallback if fetch fails
            setUser({
              id: session.user.id,
              email: session.user.email!,
              username: session.user.email?.split("@")[0] || "user",
              role: "student",
              avatar: undefined,
              is_premium: false,
              parent_id: undefined,
              linked_child_ids: [],
              first_name: undefined,
              last_name: undefined,
              subscription_tier: "free",
              subscription_status: "none",
              school_id: undefined,
              grade: undefined,
            });
          }
        } else {
          console.log("[AuthContext] No session found");
        }

        console.log("[AuthContext] Setting loading to false");
        setLoading(false);
      })
      .catch((error) => {
        console.error("[AuthContext] Error checking session:", error);
        setLoading(false);
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log("[AuthContext] Auth state changed:", _event);

      // Skip if we're in the middle of registration - let the register function handle it
      if (isRegistering.current) {
        console.log(
          "[AuthContext] Skipping auth state change during registration",
        );
        return;
      }

      // Only handle actual sign in/out events, not token refresh or other events
      if (_event === "SIGNED_IN" && session?.user) {
        console.log("[AuthContext] User signed in, fetching profile with role");

        // Fetch full profile with timeout
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Profile fetch timeout")), 5000),
        );
        Promise.race([fetchUserProfile(), timeoutPromise]).catch((err) => {
          console.warn(
            "[AuthContext] Profile fetch failed on auth change, using fallback:",
            err,
          );
          // Only set fallback if fetch fails
          setUser({
            id: session.user.id,
            email: session.user.email!,
            username: session.user.email?.split("@")[0] || "user",
            role: "student",
            avatar: undefined,
            is_premium: false,
            parent_id: undefined,
            linked_child_ids: [],
            first_name: undefined,
            last_name: undefined,
            subscription_tier: "free",
            subscription_status: "none",
            school_id: undefined,
            grade: undefined,
          });
        });
      } else if (_event === "SIGNED_OUT") {
        console.log("[AuthContext] User signed out");
        setUser(null);
      } else if (_event === "TOKEN_REFRESHED") {
        console.log("[AuthContext] Token refreshed, keeping current user");
        // Don't refetch profile on token refresh
      } else {
        console.log("[AuthContext] Other auth event, ignoring");
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
    console.log("[AuthContext] Starting login...");
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) {
      console.error("[AuthContext] Login error:", error);
      throw error;
    }

    console.log("[AuthContext] Supabase auth successful, fetching profile...");
    if (data.user) {
      // Fetch actual user profile from database - WAIT for it to complete
      try {
        await fetchUserProfile();
        console.log(
          "[AuthContext] Profile fetched successfully with role:",
          user?.role,
        );
      } catch (profileError) {
        console.error(
          "[AuthContext] Failed to fetch profile on login:",
          profileError,
        );
        // Set a basic profile from auth data as fallback
        setUser({
          id: data.user.id,
          email: data.user.email!,
          username: data.user.email?.split("@")[0] || "user",
          role: "student",
          avatar: undefined,
          is_premium: false,
          parent_id: undefined,
          linked_child_ids: [],
          first_name: undefined,
          last_name: undefined,
          subscription_tier: "free",
          subscription_status: "none",
        });
      }
    }
    console.log("[AuthContext] Login complete");
  };

  const register = async (
    email: string,
    username: string,
    password: string,
    role: "student" | "parent" | "teacher" | "school_admin" | "admin" = "student",
  ) => {
    console.log("[AuthContext] Starting registration...");
    isRegistering.current = true; // Set flag to prevent auth state change interference

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            username,
            role,
          },
        },
      });
      if (error) {
        console.error("[AuthContext] Registration error:", error);
        throw error;
      }

      console.log("[AuthContext] User created in auth:", data.user?.email);

      // Simply wait 3 seconds for trigger to complete
      console.log("[AuthContext] Waiting 3 seconds for profile creation...");
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // Try to fetch profile once with timeout
      console.log("[AuthContext] Attempting to fetch profile...");
      try {
        // Add 10 second timeout
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Profile fetch timeout")), 10000),
        );
        await Promise.race([fetchUserProfile(), timeoutPromise]);
        console.log("[AuthContext] Registration complete - profile loaded!");
      } catch (error) {
        console.warn(
          "[AuthContext] Could not fetch profile immediately:",
          error,
        );
        // Profile might not be ready yet, but user is registered
        // Set a minimal user object so they're not stuck
        if (data.user) {
          setUser({
            id: data.user.id,
            email: data.user.email!,
            username: username,
            role: role,
            avatar: undefined,
            is_premium: false,
            parent_id: undefined,
            linked_child_ids: [],
            first_name: undefined,
            last_name: undefined,
            subscription_tier: "free",
            subscription_status: "none",
          });
          console.log(
            "[AuthContext] Using temporary user object until profile loads",
          );
        }
      }
    } finally {
      isRegistering.current = false; // Clear flag when done
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
